<template>
  <div>
    <p class="text-2xl mb-1">
      Orçamentos de Volumetria
    </p>
    <p class="mb-4 grey--text text--darken-1">
      Orçar, iniciar execução e entregar o resultado. A aprovação fica com o cliente.
    </p>

    <v-card outlined>
      <v-card-title class="d-flex flex-wrap align-center justify-space-between py-3">
        <span class="text-subtitle-1">Pedidos</span>
        <v-btn
          text
          small
          color="primary"
          :loading="loading"
          @click="loadLista"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Atualizar
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loading"
          :items-per-page="25"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
            'items-per-page-text': 'Itens por página:',
          }"
          class="elevation-0"
          dense
          item-key="id"
          no-data-text="Nenhum orçamento encontrado."
          loading-text="Carregando orçamentos..."
        >
          <template #item.statusLabel="{ item }">
            <v-chip
              small
              label
              outlined
              :color="statusColor(item.statusKey)"
              class="font-weight-medium"
            >
              {{ item.statusLabel }}
            </v-chip>
          </template>

          <template #item.modoCobranca="{ item }">
            <span class="text-caption">
              {{ modoCobrancaLabel(item.modoCobranca) }}
            </span>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex flex-wrap justify-end">
              <v-btn
                v-if="canOrcar(item)"
                x-small
                color="warning"
                class="me-1 mb-1 white--text"
                :loading="actionLoadingId === item.id && actionType === 'orcar'"
                @click="openOrcar(item)"
              >
                Orçar
              </v-btn>
              <v-btn
                v-if="canIniciar(item)"
                x-small
                color="primary"
                outlined
                class="me-1 mb-1"
                :loading="actionLoadingId === item.id && actionType === 'iniciar'"
                @click="iniciarExecucao(item)"
              >
                Iniciar
              </v-btn>
              <v-btn
                v-if="canEntregar(item)"
                x-small
                color="success"
                class="me-1 mb-1"
                :loading="actionLoadingId === item.id && actionType === 'entregar'"
                @click="openEntregar(item)"
              >
                Entregar
              </v-btn>
              <v-btn
                v-if="item.resultadoUrl"
                x-small
                text
                color="primary"
                class="me-1 mb-1"
                :href="item.resultadoUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Arquivo
              </v-btn>
              <span
                v-if="!canOrcar(item) && !canIniciar(item) && !canEntregar(item) && !item.resultadoUrl"
                class="grey--text text-caption"
              >
                —
              </span>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog: Enviar orçamento -->
    <v-dialog
      v-model="orcarDialog"
      max-width="560"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="text-h6">
          Definir orçamento
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-1">
            <strong>{{ (selected && selected.nome) || '—' }}</strong>
            <span
              v-if="selected"
              class="grey--text text-caption ml-1"
            >#{{ selected.id }}</span>
          </p>
          <p
            v-if="selected && selected.descricao"
            class="mb-4 text-caption grey--text text--darken-1"
          >
            {{ selected.descricao }}
          </p>

          <p class="text-caption font-weight-bold grey--text mb-2">
            Tempo de execução estimado
          </p>
          <v-btn-toggle
            v-model="orcarForm.unidadeTempo"
            dense
            mandatory
            class="mb-3"
          >
            <v-btn
              small
              value="minutos"
            >
              Minutos
            </v-btn>
            <v-btn
              small
              value="horas"
            >
              Horas
            </v-btn>
          </v-btn-toggle>
          <v-text-field
            v-if="orcarForm.unidadeTempo === 'minutos'"
            v-model="orcarForm.tempoMin"
            label="Minutos"
            type="number"
            outlined
            dense
            hide-details="auto"
            placeholder="Ex: 90"
            class="mb-3"
          />
          <v-text-field
            v-else
            v-model="orcarForm.tempoHoras"
            label="Horas"
            type="number"
            outlined
            dense
            hide-details="auto"
            placeholder="Ex: 2.5"
            class="mb-3"
          />
          <p
            v-if="tempoEmMin > 0"
            class="text-caption grey--text mb-3"
          >
            = {{ tempoEmMin }} minutos
          </p>

          <v-text-field
            v-model="orcarForm.valorPorMinuto"
            label="Valor por minuto (R$)"
            type="number"
            step="0.01"
            outlined
            dense
            hide-details="auto"
            placeholder="Ex: 5.00"
            class="mb-3"
          />

          <v-text-field
            v-model="orcarForm.dtEntrega"
            label="Data e hora de entrega *"
            type="datetime-local"
            outlined
            dense
            hide-details="auto"
            :min="minDtEntrega"
            class="mb-2"
          />
          <p
            v-if="prazoEmDias"
            class="text-caption grey--text mb-3"
          >
            {{ prazoEmDias === 1 ? 'Entrega em até 1 dia' : `Entrega em até ${prazoEmDias} dias` }}
          </p>

          <p class="text-caption font-weight-bold grey--text mb-2">
            Adicionais de urgência
          </p>
          <div class="mb-3">
            <v-chip
              v-for="ad in adicionaisUrgencia"
              :key="ad.key"
              class="me-2 mb-2"
              small
              label
              :color="orcarForm.adicionais.includes(ad.key) ? 'warning' : undefined"
              :outlined="!orcarForm.adicionais.includes(ad.key)"
              @click="toggleAdicional(ad.key)"
            >
              {{ ad.label }} (+{{ ad.percentual }}%)
            </v-chip>
          </div>

          <v-alert
            v-if="valorBase > 0"
            dense
            text
            color="primary"
            class="mb-3"
          >
            <div class="d-flex justify-space-between mb-1">
              <span>Valor base ({{ tempoEmMin }} min × R$ {{ valorPorMinutoNum.toFixed(2) }})</span>
              <strong>{{ formatCurrency(valorBase) }}</strong>
            </div>
            <div
              v-if="valorAdicional > 0"
              class="d-flex justify-space-between mb-1 warning--text text--darken-2"
            >
              <span>Adicional urgência (+{{ percentualAdicional }}%)</span>
              <strong>+ {{ formatCurrency(valorAdicional) }}</strong>
            </div>
            <v-divider class="my-2" />
            <div class="d-flex justify-space-between">
              <strong>Total (preview)</strong>
              <strong class="primary--text">{{ formatCurrency(valorTotal) }}</strong>
            </div>
            <p class="text-caption grey--text mb-0 mt-2">
              O valor do orçamento é calculado a partir de tempo × R$/min.
            </p>
          </v-alert>

          <v-textarea
            v-model="orcarForm.observacoes"
            label="Observações para o cliente (opcional)"
            outlined
            dense
            rows="3"
            auto-grow
            hide-details="auto"
          />

          <p
            v-if="!canEnviarOrcamento"
            class="text-caption grey--text mt-3 mb-0"
          >
            Preencha tempo estimado, valor por minuto e data de entrega para enviar.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            :disabled="saving"
            @click="closeOrcarDialog"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!canEnviarOrcamento"
            @click="confirmarEnviarOrcamento"
          >
            Enviar orçamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Entregar resultado -->
    <v-dialog
      v-model="entregaDialog"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          Entregar resultado
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-1">
            <strong>{{ (selected && selected.nome) || '—' }}</strong>
            <span
              v-if="selected"
              class="grey--text text-caption ml-1"
            >#{{ selected.id }}</span>
          </p>
          <p
            v-if="selected && selected.valorLabel && selected.valorLabel !== '—'"
            class="mb-3 primary--text font-weight-bold"
          >
            Valor: {{ selected.valorLabel }}
          </p>

          <v-textarea
            v-model="entregaForm.resultadoDescricao"
            label="Descrição do resultado *"
            outlined
            dense
            rows="4"
            auto-grow
            hide-details="auto"
            placeholder="Descreva o que foi entregue..."
            class="mb-3"
          />

          <v-text-field
            v-model="entregaForm.resultadoUrl"
            label="URL do arquivo (opcional)"
            outlined
            dense
            hide-details="auto"
            placeholder="https://..."
          />
          <p class="text-caption grey--text mt-2 mb-0">
            Ao confirmar, o pedido passa para Concluído. Não há novo débito neste passo.
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            :disabled="saving"
            @click="closeEntregaDialog"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="success"
            :loading="saving"
            :disabled="!canConfirmarEntrega"
            @click="confirmarEntrega"
          >
            Confirmar entrega
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiRefresh } from '@mdi/js'
import { atualizarPersonalizadaVolumetria, listarPersonalizadasVolumetria } from '@/services/volumetryB2c'

const ADICIONAIS_URGENCIA = [
  {
    key: 'fora_expediente',
    label: 'Fora do expediente',
    percentual: 30,
  },
  {
    key: 'sabado',
    label: 'Sábado',
    percentual: 50,
  },
  {
    key: 'domingo_feriado',
    label: 'Domingo / Feriado',
    percentual: 100,
  },
]

function detectarAdicionais(dtEntrega) {
  if (!dtEntrega) return []
  const dt = new Date(dtEntrega)
  if (Number.isNaN(dt.getTime())) return []
  const diaSemana = dt.getDay()
  const hora = dt.getHours()
  if (diaSemana === 0) return ['domingo_feriado']
  if (diaSemana === 6) return ['sabado']
  if (hora < 8 || hora >= 18) return ['fora_expediente']
  return []
}

function normalizeStatusKey(raw) {
  const s = String(raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (s.includes('rejeit')) return 'rejeitado'
  if (s.includes('conclu') || s.includes('entreg')) return 'concluido'
  if (s.includes('exec') || s.includes('process')) return 'em_execucao'
  if (s.includes('aprov')) return 'aprovado'
  if (s.includes('orcamento') && (s.includes('env') || s.includes('enviad'))) {
    return 'orcamento_enviado'
  }
  if (s.includes('aguard') && s.includes('orcamento')) return 'aguardando_orcamento'
  if (s.includes('aguard') || s.includes('fila') || s.includes('pend')) return 'aguardando'
  if (s.includes('erro') || s.includes('cancel')) return 'erro'
  return s || 'aguardando'
}

function statusDisplayLabel(statusKey, fallback) {
  const map = {
    aguardando: 'Na fila',
    aguardando_orcamento: 'Aguardando orçamento',
    orcamento_enviado: 'Orçamento enviado',
    aprovado: 'Aprovado',
    rejeitado: 'Rejeitado',
    em_execucao: 'Em execução',
    concluido: 'Concluído',
    erro: 'Erro',
  }
  return map[statusKey] || fallback || '—'
}

function normalizePersonalizadaItem(row) {
  if (!row || typeof row !== 'object') return null
  if (row.TotalRegistros != null || row.totalRegistros != null) {
    if (row.Id == null && row.id == null && row.IdPersonalizada == null) return null
  }
  if (
    row.TotalAguardandoOrcamento != null
    || row.totalAguardandoOrcamento != null
  ) {
    return null
  }

  const id = row.IdPersonalizada
    ?? row.idPersonalizada
    ?? row.Id
    ?? row.id
  if (id == null || String(id).trim() === '') return null
  const n = Number(id)
  if (!Number.isFinite(n) || n <= 0) return null

  const statusRaw = row.Status ?? row.status ?? ''
  const statusKey = normalizeStatusKey(statusRaw)
  const valor = row.ValorOrcamento ?? row.valorOrcamento
  const dataRaw = row.DtSolicitacao
    ?? row.dtSolicitacao
    ?? row.CreatedAt
    ?? row.createdAt
    ?? row.DataInclusao
    ?? row.dataInclusao

  let dataLabel = '—'
  if (dataRaw) {
    const d = new Date(dataRaw)
    dataLabel = Number.isNaN(d.getTime())
      ? String(dataRaw)
      : d.toLocaleString('pt-BR')
  }

  const modoCobrancaRaw = String(
    row.ModoCobranca ?? row.modoCobranca ?? '',
  ).trim().toLowerCase()

  return {
    id: n,
    nome: row.NomeConsulta ?? row.nomeConsulta ?? row.Nome ?? row.nome ?? '—',
    descricao: row.DescricaoLivre
      ?? row.descricaoLivre
      ?? row.Descricao
      ?? row.descricao
      ?? null,
    status: statusRaw,
    statusKey,
    statusLabel: statusDisplayLabel(statusKey, statusRaw),
    valorOrcamento: valor != null ? Number(valor) : null,
    valorLabel: valor != null && Number.isFinite(Number(valor))
      ? Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—',
    modoCobranca: modoCobrancaRaw || null,
    ownerUserId: (() => {
      const saas = row.IdSaaS ?? row.idSaaS ?? row.id_saas
      if (saas == null || saas === '') return '—'
      const n = Number(saas)
      return Number.isFinite(n) && n > 0 ? n : String(saas)
    })(),
    resultadoDescricao: row.ResultadoDescricao ?? row.resultadoDescricao ?? null,
    resultadoUrl: row.ResultadoUrl ?? row.resultadoUrl ?? null,
    prazo: row.PrazoEntrega
      ?? row.prazoEntrega
      ?? row.Prazo
      ?? row.prazo
      ?? null,
    dataLabel,
    sortDate: dataRaw ? new Date(dataRaw).getTime() : 0,
  }
}

function emptyOrcarForm() {
  return {
    unidadeTempo: 'minutos',
    tempoMin: '',
    tempoHoras: '',
    valorPorMinuto: '',
    dtEntrega: '',
    adicionais: [],
    observacoes: '',
  }
}

function emptyEntregaForm() {
  return {
    resultadoDescricao: '',
    resultadoUrl: '',
  }
}

export default {
  name: 'AdminVolumetryOrcamentos',

  data() {
    return {
      icons: { mdiRefresh },
      loading: false,
      rows: [],
      orcarDialog: false,
      orcarForm: emptyOrcarForm(),
      entregaDialog: false,
      entregaForm: emptyEntregaForm(),
      selected: null,
      saving: false,
      actionLoadingId: null,
      actionType: '',
      adicionaisUrgencia: ADICIONAIS_URGENCIA,
      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Nome', value: 'nome' },
        { text: 'Cliente Ligdata', value: 'ownerUserId', width: 130 },
        { text: 'Status', value: 'statusLabel' },
        { text: 'Valor', value: 'valorLabel' },
        { text: 'Cobrança', value: 'modoCobranca' },
        { text: 'Data', value: 'dataLabel' },
        { text: 'Ações', value: 'actions', align: 'end', sortable: false },
      ],
    }
  },

  computed: {
    canConfirmarEntrega() {
      return Boolean(String(this.entregaForm.resultadoDescricao || '').trim())
    },

    tempoEmMin() {
      if (this.orcarForm.unidadeTempo === 'horas') {
        return (parseFloat(String(this.orcarForm.tempoHoras).replace(',', '.')) || 0) * 60
      }
      return parseFloat(String(this.orcarForm.tempoMin).replace(',', '.')) || 0
    },

    valorPorMinutoNum() {
      return parseFloat(String(this.orcarForm.valorPorMinuto).replace(',', '.')) || 0
    },

    valorBase() {
      return this.tempoEmMin * this.valorPorMinutoNum
    },

    percentualAdicional() {
      return (this.orcarForm.adicionais || []).reduce((acc, key) => {
        const ad = ADICIONAIS_URGENCIA.find(item => item.key === key)
        return acc + (ad ? ad.percentual : 0)
      }, 0)
    },

    valorAdicional() {
      return this.valorBase * (this.percentualAdicional / 100)
    },

    valorTotal() {
      return this.valorBase + this.valorAdicional
    },

    prazoEmDias() {
      if (!this.orcarForm.dtEntrega) return null
      const dt = new Date(this.orcarForm.dtEntrega)
      if (Number.isNaN(dt.getTime())) return null
      return Math.max(1, Math.ceil((dt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    },

    canEnviarOrcamento() {
      return this.tempoEmMin > 0
        && this.valorPorMinutoNum > 0
        && Boolean(this.orcarForm.dtEntrega)
        && this.prazoEmDias != null
    },

    minDtEntrega() {
      return new Date().toISOString().slice(0, 16)
    },
  },

  watch: {
    'orcarForm.dtEntrega'(value) {
      this.orcarForm.adicionais = detectarAdicionais(value)
    },
  },

  async mounted() {
    await this.loadLista()
  },

  methods: {
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    statusColor(statusKey) {
      if (statusKey === 'concluido') return 'teal'
      if (statusKey === 'aprovado') return 'success'
      if (statusKey === 'erro' || statusKey === 'rejeitado') return 'error'
      if (statusKey === 'em_execucao') return 'deep-purple'
      if (statusKey === 'orcamento_enviado') return 'primary'
      if (statusKey === 'aguardando' || statusKey === 'aguardando_orcamento') return 'warning'
      return 'blue-grey'
    },

    modoCobrancaLabel(modo) {
      if (modo === 'descontar_saldo') return 'Descontar saldo'
      if (modo === 'a_faturar') return 'A faturar'
      return modo || '—'
    },

    canOrcar(item) {
      return item?.statusKey === 'aguardando_orcamento'
    },

    canIniciar(item) {
      return item?.statusKey === 'aprovado'
    },

    canEntregar(item) {
      return item?.statusKey === 'aprovado' || item?.statusKey === 'em_execucao'
    },

    toggleAdicional(key) {
      const list = [...(this.orcarForm.adicionais || [])]
      const idx = list.indexOf(key)
      if (idx >= 0) list.splice(idx, 1)
      else list.push(key)
      this.orcarForm.adicionais = list
    },

    async loadLista() {
      this.loading = true
      try {
        const data = await listarPersonalizadasVolumetria({
          pagina: 1,
          tamanhoPagina: 100,
          ultimosDias: 60,
        })
        const rows = Array.isArray(data)
          ? data
          : (Array.isArray(data?.items) ? data.items : [])
        this.rows = rows
          .map(normalizePersonalizadaItem)
          .filter(Boolean)
          .sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0))
      } catch (error) {
        this.rows = []
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível carregar os orçamentos.'
        this.$notify(message, 'error')
      } finally {
        this.loading = false
      }
    },

    openOrcar(item) {
      this.selected = item
      this.orcarForm = emptyOrcarForm()
      this.orcarDialog = true
    },

    closeOrcarDialog() {
      if (this.saving) return
      this.orcarDialog = false
      this.selected = null
      this.orcarForm = emptyOrcarForm()
    },

    async confirmarEnviarOrcamento() {
      if (!this.selected?.id || !this.canEnviarOrcamento) return
      this.saving = true
      this.actionLoadingId = this.selected.id
      this.actionType = 'orcar'
      try {
        await atualizarPersonalizadaVolumetria(this.selected.id, {
          novoStatus: 'orcamento_enviado',
          tempoEstimadoMin: this.tempoEmMin,
          valorPorMinuto: this.valorPorMinutoNum,
          valorOrcamento: this.valorTotal,
          prazoEntregaDias: this.prazoEmDias,
          observacoesAdmin: String(this.orcarForm.observacoes || '').trim() || undefined,
        })
        this.$notify('Orçamento enviado com sucesso.', 'success')
        this.orcarDialog = false
        this.selected = null
        this.orcarForm = emptyOrcarForm()
        await this.loadLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível enviar o orçamento.'
        this.$notify(message, 'error')
      } finally {
        this.saving = false
        this.actionLoadingId = null
        this.actionType = ''
      }
    },

    async iniciarExecucao(item) {
      if (!item?.id) return
      this.actionLoadingId = item.id
      this.actionType = 'iniciar'
      try {
        await atualizarPersonalizadaVolumetria(item.id, {
          novoStatus: 'em_execucao',
        })
        this.$notify('Execução iniciada.', 'success')
        await this.loadLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível iniciar a execução.'
        this.$notify(message, 'error')
      } finally {
        this.actionLoadingId = null
        this.actionType = ''
      }
    },

    openEntregar(item) {
      this.selected = item
      this.entregaForm = {
        resultadoDescricao: item.resultadoDescricao || '',
        resultadoUrl: item.resultadoUrl || '',
      }
      this.entregaDialog = true
    },

    closeEntregaDialog() {
      if (this.saving) return
      this.entregaDialog = false
      this.selected = null
      this.entregaForm = emptyEntregaForm()
    },

    async confirmarEntrega() {
      if (!this.selected?.id || !this.canConfirmarEntrega) return
      const resultadoDescricao = String(this.entregaForm.resultadoDescricao || '').trim()
      const resultadoUrl = String(this.entregaForm.resultadoUrl || '').trim()
      this.saving = true
      this.actionLoadingId = this.selected.id
      this.actionType = 'entregar'
      try {
        const payload = {
          novoStatus: 'concluido',
          resultadoDescricao,
        }
        if (resultadoUrl) {
          payload.resultadoUrl = resultadoUrl
        }
        if (this.selected.modoCobranca) {
          payload.modoCobranca = this.selected.modoCobranca
        }
        await atualizarPersonalizadaVolumetria(this.selected.id, payload)
        this.$notify('Resultado entregue. Pedido concluído.', 'success')
        this.entregaDialog = false
        this.selected = null
        this.entregaForm = emptyEntregaForm()
        await this.loadLista()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Não foi possível registrar a entrega.'
        this.$notify(message, 'error')
      } finally {
        this.saving = false
        this.actionLoadingId = null
        this.actionType = ''
      }
    },
  },
}
</script>
